import { Icon, Tooltip, Typography } from '@mui/material'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import { observer } from 'mobx-react-lite'
import type { MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppIconButton } from 'src/component/common/analog/molecule/button/AppIconButton'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import { iconMapping } from 'src/util/icon-mapping'

type QuickActionState = {
  openInviteModal?: boolean
  openUploadOrganisationModal?: boolean
}

type QuickAction = {
  id: string
  label: string
  subtitle: string
  path: string
  state?: QuickActionState
}

export const TeamManagerQuickActions = observer(() => {
  const navigate = useNavigate()
  const { translation } = useLocalizationStore()
  const quickActionsTranslations = translation.pages.myTeam.quickActions

  const actions: QuickAction[] = [
    {
      id: 'users',
      label: quickActionsTranslations.users.label(),
      subtitle: quickActionsTranslations.users.subtitle(),
      path: appRoutePath.controlpanelUsers,
      state: {
        openInviteModal: true,
      },
    },
    {
      id: 'organisation',
      label: quickActionsTranslations.organisation.label(),
      subtitle: quickActionsTranslations.organisation.subtitle(),
      path: appRoutePath.controlpanelOrganisation,
      state: {
        openUploadOrganisationModal: true,
      },
    },
    {
      id: 'team-overview',
      label: quickActionsTranslations.teamOverview.label(),
      subtitle: quickActionsTranslations.teamOverview.subtitle(),
      path: appRoutePath.myTeam,
    },
    {
      id: 'profile-info',
      label: quickActionsTranslations.profileInfo.label(),
      subtitle: quickActionsTranslations.profileInfo.subtitle(),
      path: appRoutePath.profileInfo,
    },
  ]

  const handleActionClick = (action: QuickAction) => {
    navigate(action.path, action.state ? { state: action.state } : undefined)
  }

  const handleIconClick = (event: MouseEvent<HTMLButtonElement>, action: QuickAction) => {
    event.stopPropagation()
    handleActionClick(action)
  }

  const featuredActions = [
    {
      id: 'project-builder',
      label: quickActionsTranslations.projectBuilder.label(),
      subtitle: quickActionsTranslations.projectBuilder.subtitle(),
      path: appRoutePath.projectBuilder,
    },
    {
      id: 'performance-review',
      label: quickActionsTranslations.performanceReview.label(),
      subtitle: quickActionsTranslations.performanceReview.subtitle(),
      path: appRoutePath.myTeamPerformance,
    },
  ]

  return (
    <AppBox sx={styles.root}>
      <AppBox sx={styles.card}>
        <Typography variant="h3">{quickActionsTranslations.title()}</Typography>
        <Typography variant="body2" sx={styles.subtitle}>
          {quickActionsTranslations.subtitle()}
        </Typography>
        <AppBox sx={styles.actionsList}>
          {actions.map(action => (
            <AppPaper
              key={action.id}
              sx={styles.actionTile}
              onClick={() => handleActionClick(action)}
            >
              <AppBox sx={styles.actionContent}>
                <Typography variant="h6">{action.label}</Typography>
                <Typography sx={styles.tipBody}>{action.subtitle}</Typography>
                <AppBox sx={styles.actionFooter}>
                  <AppIconButton
                    sx={styles.iconButton}
                    onClick={(event: MouseEvent<HTMLButtonElement>) => handleIconClick(event, action)}
                  >
                    <Tooltip title={quickActionsTranslations.goToAction(action.label)} placement="top">
                      <Icon component={iconMapping['ArrowForwardIosRoundedIcon']} fontSize='inherit' />
                    </Tooltip>
                  </AppIconButton>
                </AppBox>
              </AppBox>
            </AppPaper>
          ))}
        </AppBox>
      </AppBox>
      <AppBox sx={styles.tipBanner}>
        <AppBox sx={{ flexDirection: 'row', gap: 10 }}>
          <AppBox sx={styles.tipIcon}>
            <LightbulbOutlinedIcon sx={styles.tipIconSvg} />
          </AppBox>
          <AppBox sx={styles.tipText}>
            <Typography variant="h6">{quickActionsTranslations.quickTip.title()}</Typography>
            <Typography sx={styles.tipBody}>{quickActionsTranslations.quickTip.body()}</Typography>
          </AppBox>
        </AppBox>
        <AppBox sx={styles.featuredActionsList}>
          {featuredActions.map(action => (
            <AppBox
              key={action.id}
              sx={styles.featuredActionItem}
              onClick={() => handleActionClick(action)}
            >
              <AppBox sx={styles.featuredActionContent}>
                <Typography variant="caption">{action.label}</Typography>
                <AppIconButton
                  sx={styles.featuredIconButton}
                  onClick={(event: MouseEvent<HTMLButtonElement>) => handleIconClick(event, action)}
                >
                  <Tooltip title={quickActionsTranslations.goToAction(action.label)} placement="top">
                    <Icon component={iconMapping['ArrowForwardIosRoundedIcon']} fontSize='inherit' />
                  </Tooltip>
                </AppIconButton>
              </AppBox>
            </AppBox>
          ))}
        </AppBox>
      </AppBox>
    </AppBox>
  )
})

const styles = {
  root: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: 20,
  },
  card: {
    width: { xs: '100%', md: '70%' },
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    borderRadius: '12px',
    padding: '10px 15px',
    backgroundColor: 'background.paper',
  },
  subtitle: {
    color: 'text.secondary',
    marginTop: '-20px',
  },
  actionsList: {
    display: 'flex',
    gap: 20,
    flexWrap: 'wrap',
  },
  actionTile: {
    padding: '10px',
    flex: '1 1 calc(25% - 16px)',
    minWidth: '140px',
    backgroundColor: ColorTokens.primary10,
    border: 0,
    cursor: 'pointer',
  },
  actionContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  actionFooter: {
    marginTop: 'auto',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  iconButton: {
    backgroundColor: 'background.paper',
    width: 28,
    height: 28,
    padding: 0.5,
    marginTop: '5px',
    '& .MuiSvgIcon-root': {
      fontSize: 14,
      color: ColorTokens.primary500,
    },
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  tipBanner: {
    width: { xs: '100%', md: '30%' },
    flexDirection: 'column',
    gap: 20,
    borderRadius: '12px',
    padding: '10px 15px',
    backgroundColor: ColorTokens.primary10,
  },
  tipIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'background.paper',
    flexShrink: 0,
  },
  tipIconSvg: {
    fontSize: 20,
    color: ColorTokens.primary500,
  },
  tipText: {
    display: 'flex',
    flexDirection: 'column',
  },
  tipBody: {
    fontSize: 12,
    color: 'text.secondary',
  },
  featuredActionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  featuredActionItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 15px',
    cursor: 'pointer',
    borderRadius: '10px',
    backgroundColor: 'background.paper',
  },
  featuredActionContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: 10,
  },
  featuredIconButton: {
    width: 24,
    height: 24,
    padding: 0,
    flexShrink: 0,
    backgroundColor: 'background.dark',
    '& .MuiSvgIcon-root': {
      fontSize: 12,
      color: ColorTokens.primary500,
    },
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
}
